import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Truck, Loader2 } from "lucide-react";

/**
 * ZIP-based shipping estimate widget for the drop detail page.
 *
 * If the user is a logged-in member with free shipping, we skip the widget
 * entirely and show a "Free member shipping" chip instead.
 *
 * Otherwise: single ZIP input → server-side ZIP lookup + Shippo quote →
 * "Estimated $X.XX via UPS Ground · 2 days to 78701". Remembers the last
 * ZIP the customer entered in localStorage.
 */
export default function ShippingEstimateWidget({ isMember, onResolved }) {
  const [zip, setZip] = useState(() => localStorage.getItem("ff2d_est_zip") || "");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const runQuote = async (zipCode) => {
    const clean = (zipCode || "").trim().slice(0, 10);
    if (clean.length < 3) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/shipping/estimate-by-zip", { zip: clean });
      setQuote(data);
      localStorage.setItem("ff2d_est_zip", clean);
      // Notify parent so it can render the shipment preview.
      onResolved?.({ zip: clean, state: data?.place?.state, warehouse_id: data?.warehouse?.id });
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not estimate right now");
    } finally {
      setBusy(false);
    }
  };

  // Auto-quote if we have a remembered ZIP on mount
  useEffect(() => {
    if (zip && zip.length === 5) runQuote(zip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isMember) {
    return (
      <div className="card-flat p-4" data-testid="ship-estimate-member">
        <div className="flex items-center gap-2 text-sm">
          <Truck size={16} className="text-[var(--ff-primary)]" />
          <p className="text-[var(--ff-text-primary)] font-medium">Free cold-chain shipping</p>
        </div>
        <p className="text-xs text-[var(--ff-text-muted)] mt-1">Included with every member drop — no code needed.</p>
      </div>
    );
  }

  const c = quote?.cheapest;
  const isReal = quote?.source === "shippo";
  const place = quote?.place;
  const warehouse = quote?.warehouse;

  return (
    <div className="card-flat p-4" data-testid="ship-estimate-widget">
      <div className="flex items-center gap-2">
        <Truck size={16} className="text-[var(--ff-primary)]" />
        <p className="overline">Estimate shipping</p>
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); runQuote(zip); }}
        className="mt-3 flex items-center gap-2"
      >
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          placeholder="Your ZIP"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
          className="input-base flex-1 text-sm tabular-nums"
          data-testid="ship-estimate-zip"
        />
        <button
          type="submit"
          disabled={busy || zip.length < 3}
          className="btn-secondary text-xs px-3 py-2 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="ship-estimate-submit"
        >
          {busy ? <Loader2 size={12} className="animate-spin"/> : "Check"}
        </button>
      </form>

      {err && <p className="mt-2 text-xs text-red-700" data-testid="ship-estimate-error">{err}</p>}

      {c && !busy && (
        <div className="mt-3" data-testid="ship-estimate-result">
          <p className="font-display text-2xl tabular-nums text-[var(--ff-primary)]">
            ~${Number(c.amount).toFixed(2)}
          </p>
          <p className="text-xs text-[var(--ff-text-secondary)] mt-1 leading-snug">
            {isReal
              ? <>
                  {c.provider} {c.service}
                  {c.estimated_days ? ` · ${c.estimated_days}-day` : ""}
                  {place ? ` to ${place.city}, ${place.state} ${place.zip}` : ""}
                </>
              : <>Flat-rate cold-chain shipping estimate. Actual rate calculated at checkout.</>
            }
          </p>
          {warehouse && (
            <p className="text-[10px] text-[var(--ff-text-muted)] mt-1.5 inline-flex items-center gap-1" data-testid="ship-estimate-warehouse">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"/>
              Shipping from our {warehouse.name.replace(" Fulfillment Center", "")} warehouse
            </p>
          )}
          {!isMember && (
            <p className="text-[11px] text-[var(--ff-text-muted)] mt-2">
              Members ship free on every order.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
